import {
  KeyboardEvent as ReactKeyboardEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import styled from 'styled-components';
import {Button} from '@ui/atoms/button';
import {CancelIcon} from '@ui/atoms/icons';
import {lockElementScroll} from '@utils/scroll-lock';
import {
  BasicsStep,
  CadenceStep,
  ContentStep,
} from '../create-checkin-cycle/components';
import {
  CreateCheckinCycleFormData,
  SelectOption,
} from '../create-checkin-cycle/components/types';
import {
  EditableCycleSection,
  isCycleSectionValid,
} from '../create-checkin-cycle/components/cycle-form-validation';
import {
  cloneFormData,
  hasFormDataChanged,
} from '../create-checkin-cycle/components/review-form-state';
import {CycleEditConfirmationModal} from '../create-checkin-cycle/components/cycle-edit-confirmation-modal';

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1200;
  display: flex;
  justify-content: flex-end;
  background: rgba(36, 36, 36, 0.56);
`;

const Panel = styled.aside`
  position: fixed;
  z-index: 1201;
  top: 0;
  right: 0;
  display: flex;
  width: min(560px, 100vw);
  height: 100vh;
  flex-direction: column;
  background: ${({theme}) => theme.colors.white};
  box-shadow: -8px 0 32px rgba(30, 30, 47, 0.12);

  @media (max-width: 560px) {
    width: 100vw;
  }
`;

const Header = styled.header`
  display: flex;
  min-height: 80px;
  align-items: center;
  justify-content: space-between;
  flex: 0 0 auto;
  gap: 24px;
  border-bottom: 1px solid ${({theme}) => theme.colors.borderLight};
  padding: 18px 24px;

  @media (max-width: 560px) {
    padding: 14px 20px;
  }
`;

const CloseButton = styled.button`
  display: inline-flex;
  width: 103px;
  height: 42px;
  align-items: center;
  justify-content: space-between;
  flex: 0 0 auto;
  border: 1px solid ${({theme}) => theme.colors.borderDark};
  border-radius: 10px;
  background: ${({theme}) => theme.colors.white};
  padding: 10px 16px;
  color: ${({theme}) => theme.colors.blackTwo};
  font: 600 14px/22px Graphik, sans-serif;

  svg {
    width: 16px;
    height: 16px;
    flex: 0 0 auto;
  }

  &:focus-visible {
    outline: 2px solid ${({theme}) => theme.colors.purple300};
    outline-offset: 2px;
  }
`;

const ScrollArea = styled.div`
  min-height: 0;
  flex: 1;
  overflow-y: auto;
  padding: 24px 24px 32px;

  @media (max-width: 560px) {
    padding: 20px 20px 28px;
  }
`;

const Form = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const Title = styled.h2`
  margin: 0;
  color: ${({theme}) => theme.colors.blackTwo};
  font: 600 20px/28px Graphik, sans-serif;
`;

const Footer = styled.footer`
  border-top: 1px solid ${({theme}) => theme.colors.borderLight};
  background: ${({theme}) => theme.colors.white};
  padding: 16px 24px 24px;

  @media (max-width: 560px) {
    padding: 16px 20px calc(20px + env(safe-area-inset-bottom));
  }
`;

interface CycleEditSidecarProps {
  section: EditableCycleSection;
  data: CreateCheckinCycleFormData;
  groupOptions: SelectOption[];
  peopleOptions: SelectOption[];
  isLoadingGroups?: boolean;
  audienceError?: string | null;
  onRetryAudience?: () => void;
  cycleStatus: string;
  isSaving?: boolean;
  onClose: () => void;
  onSave: (data: CreateCheckinCycleFormData) => Promise<boolean>;
}

const sectionTitle: Record<EditableCycleSection, string> = {
  basics: 'Edit Basics',
  content: 'Edit Content',
  cadence: 'Edit Cadence',
};

export const CycleEditSidecar = ({
  section,
  data,
  groupOptions,
  peopleOptions,
  isLoadingGroups,
  audienceError,
  onRetryAudience,
  cycleStatus,
  isSaving = false,
  onClose,
  onSave,
}: CycleEditSidecarProps) => {
  const panelRef = useRef<HTMLElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const onCloseRef = useRef(onClose);
  const confirmationRef = useRef(false);
  const [draft, setDraft] = useState(() => cloneFormData(data));
  const [snapshot, setSnapshot] = useState(() => cloneFormData(data));
  const [showConfirmation, setShowConfirmation] = useState(false);

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    confirmationRef.current = showConfirmation;
  }, [showConfirmation]);

  useEffect(() => {
    const next = cloneFormData(data);
    setDraft(next);
    setSnapshot(next);
    setShowConfirmation(false);
    // A background refetch or late audience-label load must not overwrite typing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [section]);

  useEffect(() => {
    const releaseScrollLock = lockElementScroll(document.body);
    closeButtonRef.current?.focus();

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !confirmationRef.current) {
        onCloseRef.current();
      }
    };
    document.addEventListener('keydown', onKeyDown);

    return () => {
      releaseScrollLock();
      document.removeEventListener('keydown', onKeyDown);
    };
  }, []);

  const hasChanges = useMemo(() => hasFormDataChanged(draft, snapshot), [
    draft,
    snapshot,
  ]);
  const isValid = useMemo(() => isCycleSectionValid(section, draft), [
    draft,
    section,
  ]);
  const needsConfirmation =
    cycleStatus === 'active' || cycleStatus === 'paused';

  const persist = async () => {
    const saved = await onSave(draft);
    if (saved) setShowConfirmation(false);
  };

  const requestSave = () => {
    if (!hasChanges || !isValid || isSaving) return;
    if (needsConfirmation) {
      setShowConfirmation(true);
      return;
    }
    persist();
  };

  const trapFocus = (event: ReactKeyboardEvent<HTMLElement>) => {
    if (event.key !== 'Tab') return;
    const focusable = panelRef.current?.querySelectorAll<HTMLElement>(
      'button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
    );
    if (!focusable?.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  return (
    <Overlay>
      <Panel
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="cycle-edit-title"
        onKeyDown={trapFocus}>
        <Header>
          <Title id="cycle-edit-title">{sectionTitle[section]}</Title>
          <CloseButton ref={closeButtonRef} type="button" onClick={onClose}>
            <CancelIcon />
            Close
          </CloseButton>
        </Header>
        <ScrollArea>
          <Form>
            {section === 'basics' && (
              <BasicsStep
                compact
                cycleName={draft.cycleName}
                includeScope={draft.includeScope}
                excludeScope={draft.excludeScope}
                includeGroups={draft.includeGroups}
                includePeople={draft.includePeople}
                excludeGroups={draft.excludeGroups}
                excludePeople={draft.excludePeople}
                groupOptions={groupOptions}
                peopleOptions={peopleOptions}
                isLoadingGroups={isLoadingGroups}
                audienceError={audienceError}
                onRetryAudience={onRetryAudience}
                onCycleNameChange={(cycleName) =>
                  setDraft((current) => ({...current, cycleName}))
                }
                onIncludeScopeChange={(includeScope) =>
                  setDraft((current) => ({...current, includeScope}))
                }
                onExcludeScopeChange={(excludeScope) =>
                  setDraft((current) => ({...current, excludeScope}))
                }
                onIncludeGroupsChange={(includeGroups) =>
                  setDraft((current) => ({...current, includeGroups}))
                }
                onIncludePeopleChange={(includePeople) =>
                  setDraft((current) => ({...current, includePeople}))
                }
                onExcludeGroupsChange={(excludeGroups) =>
                  setDraft((current) => ({...current, excludeGroups}))
                }
                onExcludePeopleChange={(excludePeople) =>
                  setDraft((current) => ({...current, excludePeople}))
                }
              />
            )}

            {section === 'content' && (
              <ContentStep
                compact
                blocks={draft.contentBlocks}
                onBlocksChange={(contentBlocks) =>
                  setDraft((current) => ({...current, contentBlocks}))
                }
                showValidation
                visibility={draft.visibility}
                onVisibilityChange={(visibility) =>
                  setDraft((current) => ({...current, visibility}))
                }
              />
            )}

            {section === 'cadence' && (
              <CadenceStep
                compact
                value={draft.cadence}
                onChange={(cadence) =>
                  setDraft((current) => ({...current, cadence}))
                }
              />
            )}
          </Form>
        </ScrollArea>
        <Footer>
          <Button
            width="full"
            type="button"
            isLoading={isSaving}
            disabled={!hasChanges || !isValid || isSaving}
            onClick={requestSave}>
            Save changes
          </Button>
        </Footer>
      </Panel>

      <CycleEditConfirmationModal
        isOpen={showConfirmation}
        onClose={() => setShowConfirmation(false)}
        onConfirm={persist}
        isLoading={isSaving}
        cycleStatus={cycleStatus === 'paused' ? 'paused' : 'active'}
        zIndex={1300}
      />
    </Overlay>
  );
};
